/** Wire ⇄ draft mapping for rule rows.
 *
 * `draftsFromDetail` → `rulesToWrite` must round-trip an untouched row
 * byte-identically (the save is a full-list reconcile — every row is sent,
 * edited or not — and a drifting field would count as an edit on both the
 * dashboard and the nfchange history). Unit-tested against dashboard-produced
 * fixtures in `ruleSerialize.test.ts`.
 */
import type { RulesetBrief, RuleWire, RuleWriteRow } from '@/api/notificationRulesets';
import { RULE_TYPE_EXPR, RULE_TYPE_TPL } from '@/api/notificationRulesets';

import { defaultArgs, TPL } from './ruleCatalog';
import type { NestedRulesetRef, RuleDraft } from './ruleTypes';

let nextClientId = -1;

/** Fresh negative client id for a new row (created on save). */
export function allocClientRuleId(): number {
  return nextClientId--;
}

export function draftsFromDetail(
  rules: RuleWire[],
  refs?: { rulesets?: Record<string, RulesetBrief> },
): RuleDraft[] {
  const rulesets = refs?.rulesets ?? {};
  return rules.map((r) => {
    let nested: NestedRulesetRef | null = null;
    if (r.nested_ruleset !== null && r.nested_ruleset !== undefined) {
      const brief = rulesets[String(r.nested_ruleset)];
      nested = brief
        ? { id: brief.id, name: brief.name, codename: brief.codename, match_type: brief.match_type }
        : { id: r.nested_ruleset };
    }
    return {
      id: r.id,
      type: r.type,
      tpl: r.tpl,
      args: r.args,
      negate: r.negate,
      field: r.field,
      op: r.op,
      value: r.value,
      nested_ruleset: nested,
    };
  });
}

/** The COMPLETE ordered write list. Negative ids → `id: null` CREATE rows. */
export function rulesToWrite(drafts: RuleDraft[]): RuleWriteRow[] {
  return drafts.map((d) => {
    let args = d.args;
    // The tpl-56 matcher does an unguarded `args["allow_undefined"]` read on
    // the backend — never ship delivery_methods without it.
    if (
      d.type === RULE_TYPE_TPL &&
      d.tpl === TPL.DELIVERY_METHOD &&
      args &&
      typeof args === 'object' &&
      'delivery_methods' in args &&
      !('allow_undefined' in args)
    ) {
      args = { ...args, allow_undefined: false };
    }
    return {
      id: d.id > 0 ? d.id : null,
      type: d.type,
      tpl: d.tpl,
      args,
      negate: d.negate,
      field: d.field,
      op: d.op,
      value: d.value,
      nested_ruleset: d.nested_ruleset ? { id: d.nested_ruleset.id } : null,
    };
  });
}

/** A new template rule with per-tpl default args seeded. */
export function newRule(tpl: number): RuleDraft {
  return {
    id: allocClientRuleId(),
    type: RULE_TYPE_TPL,
    tpl,
    args: defaultArgs(tpl),
    negate: false,
    field: '',
    op: 0,
    value: '',
    nested_ruleset: null,
  };
}

/** A new expression (advanced) rule. */
export function newExprRule(): RuleDraft {
  return {
    id: allocClientRuleId(),
    type: RULE_TYPE_EXPR,
    tpl: 0,
    args: null,
    negate: false,
    field: '',
    op: 0,
    value: '',
    nested_ruleset: null,
  };
}

/** Switch a rule's template, resetting args to the new template's defaults
 *  (stale keys are wire-safe but a clean object avoids the tpl-56 trap and
 *  keeps history diffs readable). */
export function withTemplate(rule: RuleDraft, tpl: number): RuleDraft {
  return { ...rule, type: RULE_TYPE_TPL, tpl, args: defaultArgs(tpl), nested_ruleset: null };
}

// ---------------------------------------------------------------------------
// Time helpers (trip-time-range stores seconds, the UI edits minutes)
// ---------------------------------------------------------------------------

export function minutesFromSeconds(seconds: unknown): number {
  const n = typeof seconds === 'number' ? seconds : Number(seconds ?? 0);
  return Number.isFinite(n) ? Math.round(n / 60) : 0;
}

export function secondsFromMinutes(minutes: string | number): number {
  const n = typeof minutes === 'number' ? minutes : Number(minutes);
  return Number.isFinite(n) ? Math.round(n * 60) : 0;
}

// ---------------------------------------------------------------------------
// Datetime bounds for tpls 67/68/69 (dashboard-exact wire formats)
// ---------------------------------------------------------------------------

const pad = (n: number, w = 2) => String(n).padStart(w, '0');

export type DtTz = 'utc' | 'local';

/** `args.tz` — which clock the bounds are written in (backend `dt_match.py`).
 *  A rule without it keeps its template's historical mode: UTC for datetime,
 *  the value's own (local) clock for date and time. */
export function effectiveDtTz(tpl: number, tz: unknown): DtTz {
  if (tz === 'utc' || tz === 'local') return tz;
  return tpl === TPL.DATETIME_WITHIN_RANGE ? 'utc' : 'local';
}

const ISO_DT = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,6}))?)?(Z|[+-]\d{2}:?\d{2})?$/i;

function offsetMinutes(off: string): number {
  if (off.toUpperCase() === 'Z') return 0;
  const sign = off[0] === '-' ? -1 : 1;
  const digits = off.slice(1).replace(':', '');
  return sign * (Number(digits.slice(0, 2)) * 60 + Number(digits.slice(2, 4)));
}

/** A Date whose LOCAL fields are the given wall clock — what the picker shows.
 *  The date-fns adapter (MUI X v7) has no `timezone` support, so the editor
 *  shifts the clock instead of the zone. Caveat: a wall clock inside the
 *  browser's own DST spring-forward gap cannot exist locally and moves by the
 *  gap. */
function wallClockDate(y: number, mo: number, d: number, h: number, mi: number, s: number): Date {
  return new Date(y, mo, d, h, mi, s, 0);
}

/** Serialize a picker Date to the tpl's wire format:
 *  67 → utc: ISO-8601 instant of the shown wall clock read as UTC (`…Z`);
 *       local: the shown wall clock without offset (`YYYY-MM-DDTHH:mm:ss`);
 *  68 → `YYYY-MM-DD`, 69 → `HH:mm:ss` (plain strings, no clock to convert). */
export function serializeDtBound(tpl: number, date: Date | null, tz?: unknown): string | null {
  if (!date || Number.isNaN(date.getTime())) return null;
  const ymd = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  const hms = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  if (tpl === TPL.DATETIME_WITHIN_RANGE) {
    if (effectiveDtTz(tpl, tz) === 'local') return `${ymd}T${hms}`;
    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()),
    ).toISOString();
  }
  if (tpl === TPL.DATE_WITHIN_RANGE) return ymd;
  return hms;
}

/** Parse a stored bound back into a picker Date (null on absent/garbage).
 *  For 67 the Date's local fields are the wall clock to show: the UTC time in
 *  utc mode (a bound without offset is UTC, as the matcher reads it), the time
 *  as written in local mode (an offset is ignored, as the matcher ignores it). */
export function parseDtBound(tpl: number, raw: unknown, tz?: unknown): Date | null {
  if (typeof raw !== 'string' || !raw) return null;
  if (tpl === TPL.TIME_WITHIN_RANGE) {
    const m = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(raw);
    if (!m) return null;
    const d = new Date();
    d.setHours(Number(m[1]), Number(m[2]), Number(m[3] ?? 0), 0);
    return d;
  }
  if (tpl === TPL.DATE_WITHIN_RANGE) {
    // Parse as a LOCAL date — `new Date("YYYY-MM-DD")` would be UTC midnight
    // and shift a day when re-serialized with local getters.
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
    if (!m) return null;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  const m = ISO_DT.exec(raw);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const h = Number(m[4]);
  const mi = Number(m[5]);
  const s = Number(m[6] ?? 0);
  const offset = m[8];
  if (effectiveDtTz(tpl, tz) === 'local' || !offset) return wallClockDate(y, mo, d, h, mi, s);
  const utc = new Date(Date.UTC(y, mo, d, h, mi, s) - offsetMinutes(offset) * 60_000);
  return wallClockDate(
    utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate(), utc.getUTCHours(), utc.getUTCMinutes(), utc.getUTCSeconds(),
  );
}

/** Rewrite a stored bound for another `tz`, keeping the wall clock the editor shows. */
export function retzDtBound(tpl: number, fromTz: unknown, toTz: unknown, raw: unknown): unknown {
  if (tpl !== TPL.DATETIME_WITHIN_RANGE || typeof raw !== 'string' || !raw) return raw;
  return serializeDtBound(tpl, parseDtBound(tpl, raw, fromTz), toTz) ?? raw;
}
